import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  BuildingOfficeIcon,
  UsersIcon,
  UserGroupIcon,
  ChartBarIcon,
  PlusIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  ShoppingCartIcon,
  CurrencyDollarIcon,
  CubeIcon,
} from '@heroicons/react/24/outline';
import { usersAPI, companiesAPI, ordersAPI, productsAPI, salesAPI } from '../services/api';
import { useTranslation } from 'react-i18next';
import { User, Company } from '../types';
import { useCompany } from './CompanyContext';
import { useUser } from './UserContext';

const Dashboard: React.FC = () => {
  const { selectedCompany } = useCompany();
  const { isAdmin, currentUser } = useUser();
  const { t, i18n } = useTranslation();
  const [stats, setStats] = useState({
    totalCompanies: 0,
    totalUsers: 0,
    activeUsers: 0,
    totalEmployees: 0,
    totalOrders: 0,
    pendingOrders: 0,
    totalProducts: 0,
    lowStockProducts: 0,
    totalSales: 0,
    monthlySales: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadStats();
  }, [selectedCompany, isAdmin]);

  const loadStats = async () => {
    try {
      setLoading(true);
      
      if (selectedCompany) {
        // Load company-specific stats
        const [users, orders, products, sales] = await Promise.all([
          usersAPI.getByCompany(selectedCompany.id),
          ordersAPI.getByCompany(selectedCompany.id),
          productsAPI.getByCompany(selectedCompany.id),
          salesAPI.getByCompany(selectedCompany.id),
        ]);

        const activeUsers = users.filter(user => user.status === 'active').length;
        const employees = users.filter(user => user.role !== 'admin').length;
        const pendingOrders = orders.filter(order => order.status === 'assigned' || order.status === 'in_progress').length;
        const lowStockProducts = products.filter(product => product.inventory <= product.minInventory).length;
        
        // Calculate monthly sales (last 30 days)
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        const monthlySales = sales
          .filter(sale => new Date(sale.createdAt) >= thirtyDaysAgo)
          .reduce((sum, sale) => sum + sale.total, 0);

        setStats({
          totalCompanies: 1, // Only showing current company
          totalUsers: users.length,
          activeUsers,
          totalEmployees: employees,
          totalOrders: orders.length,
          pendingOrders,
          totalProducts: products.length,
          lowStockProducts,
          totalSales: sales.length,
          monthlySales,
        });
      } else if (isAdmin) {
        // Load system-wide stats for admin
        const [users, companies] = await Promise.all([
          usersAPI.getAll(),
          companiesAPI.getAll(),
        ]);

        const activeUsers = users.filter(user => user.status === 'active').length;
        const employees = users.filter(user => user.role !== 'admin').length;

        setStats({
          totalCompanies: companies.length,
          totalUsers: users.length,
          activeUsers,
          totalEmployees: employees,
          totalOrders: 0,
          pendingOrders: 0,
          totalProducts: 0,
          lowStockProducts: 0,
          totalSales: 0,
          monthlySales: 0,
        });
      } else {
        // No company selected and not admin
        setStats({
          totalCompanies: 0,
          totalUsers: 0,
          activeUsers: 0,
          totalEmployees: 0,
          totalOrders: 0,
          pendingOrders: 0,
          totalProducts: 0,
          lowStockProducts: 0,
          totalSales: 0,
          monthlySales: 0,
        });
      }
    } catch (err) {
      setError(t('dashboard.failedStats'));
      console.error('Error loading stats:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-200 border-t-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-danger-50 border border-danger-200 rounded-xl p-6">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <div className="w-8 h-8 bg-danger-100 rounded-full flex items-center justify-center">
              <svg className="w-5 h-5 text-danger-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-danger-800">{t('dashboard.errorLoading')}</h3>
            <p className="text-sm text-danger-700 mt-1">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  const StatCard = ({ title, value, icon: Icon, color, change, href, subtitle }: any) => (
    <div className="bg-white rounded-xl shadow-soft border border-gray-200 overflow-hidden hover:shadow-medium transition-all duration-300 group">
      <div className="p-6">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <p className="text-sm font-medium text-gray-600 truncate">{title}</p>
            <p className="mt-2 text-3xl font-bold text-gray-900">{value}</p>
            {subtitle && (
              <p className="mt-1 text-sm text-gray-500">{subtitle}</p>
            )}
            {change && (
              <div className="mt-2 flex items-center">
                {change > 0 ? (
                  <ArrowUpIcon className="w-4 h-4 text-success-500" />
                ) : (
                  <ArrowDownIcon className="w-4 h-4 text-danger-500" />
                )}
                <span className={`ml-1 text-sm font-medium ${
                  change > 0 ? 'text-success-600' : 'text-danger-600'
                }`}>
                  {Math.abs(change)}%
                </span>
                <span className="ml-2 text-sm text-gray-500">{t('dashboard.stats.fromLastMonth')}</span>
              </div>
            )}
          </div>
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${color}`}>
            <Icon className="w-6 h-6 text-white" />
          </div>
        </div>
      </div>
      {href && (
        <div className="bg-gray-50 px-6 py-3 border-t border-gray-200">
          <Link
            to={href}
            className="text-sm font-medium text-primary-600 hover:text-primary-700 group-hover:text-primary-800 transition-colors duration-200"
          >
            {t('actions.viewDetails', 'View details')} →
          </Link>
        </div>
      )}
    </div>
  );

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat(i18n.language || 'en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-soft border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{t('dashboard.title')}</h1>
            <p className="mt-2 text-gray-600">
              {selectedCompany 
                ? t('dashboard.welcomeCompany', { company: selectedCompany.name })
                : isAdmin 
                  ? t('dashboard.welcomeAdmin')
                  : t('dashboard.selectCompany')
              }
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <div className="w-3 h-3 bg-success-500 rounded-full animate-pulse"></div>
            <span className="text-sm text-gray-500">{t('dashboard.systemOnline')}</span>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {selectedCompany ? (
          // Company-specific stats
          <>
            <StatCard
              title={t('dashboard.stats.totalOrders')}
              value={stats.totalOrders}
              subtitle={t('dashboard.stats.pending', { count: stats.pendingOrders })}
              icon={ShoppingCartIcon}
              color="bg-gradient-to-br from-blue-500 to-blue-600"
              href="/orders"
            />
            <StatCard
              title={t('dashboard.stats.totalProducts')}
              value={stats.totalProducts}
              subtitle={t('dashboard.stats.lowStock', { count: stats.lowStockProducts })}
              icon={CubeIcon}
              color="bg-gradient-to-br from-green-500 to-green-600"
              href="/products"
            />
            <StatCard
              title={t('dashboard.stats.totalSales')}
              value={stats.totalSales}
              subtitle={t('dashboard.stats.thisMonth', { amount: formatCurrency(stats.monthlySales) })}
              icon={CurrencyDollarIcon}
              color="bg-gradient-to-br from-purple-500 to-purple-600"
              href="/sales"
            />
            <StatCard
              title={t('dashboard.stats.totalEmployees')}
              value={stats.totalEmployees}
              subtitle={`${stats.activeUsers} ${t('user.activeUsers', 'active users')}`}
              icon={UserGroupIcon}
              color="bg-gradient-to-br from-orange-500 to-orange-600"
              href="/employees"
            />
          </>
        ) : isAdmin ? (
          // System-wide stats for admin
          <>
            <StatCard
              title={t('dashboard.stats.totalCompanies')}
              value={stats.totalCompanies}
              icon={BuildingOfficeIcon}
              color="bg-gradient-to-br from-blue-500 to-blue-600"
              change={12}
              href="/companies"
            />
            <StatCard
              title={t('nav.users')}
              value={stats.totalUsers}
              icon={UsersIcon}
              color="bg-gradient-to-br from-purple-500 to-purple-600"
              change={8}
              href="/users"
            />
            <StatCard
              title={t('dashboard.stats.totalEmployees')}
              value={stats.totalEmployees}
              icon={UserGroupIcon}
              color="bg-gradient-to-br from-green-500 to-green-600"
              change={15}
              href="/employees"
            />
            <StatCard
              title={t('dashboard.stats.activeUsers')}
              value={stats.activeUsers}
              icon={ChartBarIcon}
              color="bg-gradient-to-br from-orange-500 to-orange-600"
              change={-3}
              href="/users"
            />
          </>
        ) : (
          // No company selected and not admin
          <div className="col-span-4 text-center py-12">
            <BuildingOfficeIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No company selected</h3>
            <p className="mt-1 text-sm text-gray-500">Please select a company to view your dashboard.</p>
          </div>
        )}
      </div>

      {/* Quick Actions */}
      {selectedCompany && (
        <div className="bg-white rounded-xl shadow-soft border border-gray-200 overflow-hidden">
          <div className="px-6 py-5 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">{t('dashboard.quickActions.title')}</h3>
            <p className="mt-1 text-sm text-gray-600">{t('dashboard.quickActions.subtitle')}</p>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <Link
                to="/orders/new"
                className="group relative rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 p-6 hover:border-primary-300 hover:bg-primary-50 transition-all duration-200"
              >
                <div className="flex items-center space-x-4">
                  <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center group-hover:bg-primary-200 transition-colors duration-200">
                    <PlusIcon className="w-5 h-5 text-primary-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 group-hover:text-primary-700">{t('dashboard.quickActions.createOrder.title')}</p>
                    <p className="text-sm text-gray-500 group-hover:text-primary-600">{t('dashboard.quickActions.createOrder.desc')}</p>
                  </div>
                </div>
              </Link>

              <Link
                to="/products/new"
                className="group relative rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 p-6 hover:border-primary-300 hover:bg-primary-50 transition-all duration-200"
              >
                <div className="flex items-center space-x-4">
                  <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center group-hover:bg-primary-200 transition-colors duration-200">
                    <PlusIcon className="w-5 h-5 text-primary-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 group-hover:text-primary-700">{t('dashboard.quickActions.addProduct.title')}</p>
                    <p className="text-sm text-gray-500 group-hover:text-primary-600">{t('dashboard.quickActions.addProduct.desc')}</p>
                  </div>
                </div>
              </Link>

              <Link
                to="/sales"
                className="group relative rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 p-6 hover:border-primary-300 hover:bg-primary-50 transition-all duration-200"
              >
                <div className="flex items-center space-x-4">
                  <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center group-hover:bg-primary-200 transition-colors duration-200">
                    <PlusIcon className="w-5 h-5 text-primary-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 group-hover:text-primary-700">View Sales</p>
                    <p className="text-sm text-gray-500 group-hover:text-primary-600">Review and manage sales records</p>
                  </div>
                </div>
              </Link>
            </div>
          </div>
        </div>
      )}

      {isAdmin && !selectedCompany && (
        <div className="bg-white rounded-xl shadow-soft border border-gray-200 overflow-hidden">
          <div className="px-6 py-5 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">{t('dashboard.quickActions.title')}</h3>
            <p className="mt-1 text-sm text-gray-600">{t('dashboard.quickActions.subtitle')}</p>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <Link
                to="/companies/new"
                className="group relative rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 p-6 hover:border-primary-300 hover:bg-primary-50 transition-all duration-200"
              >
                <div className="flex items-center space-x-4">
                  <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center group-hover:bg-primary-200 transition-colors duration-200">
                    <PlusIcon className="w-5 h-5 text-primary-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 group-hover:text-primary-700">{t('dashboard.quickActions.addCompany.title')}</p>
                    <p className="text-sm text-gray-500 group-hover:text-primary-600">{t('dashboard.quickActions.addCompany.desc')}</p>
                  </div>
                </div>
              </Link>

              <Link
                to="/users/new"
                className="group relative rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 p-6 hover:border-primary-300 hover:bg-primary-50 transition-all duration-200"
              >
                <div className="flex items-center space-x-4">
                  <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center group-hover:bg-primary-200 transition-colors duration-200">
                    <PlusIcon className="w-5 h-5 text-primary-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 group-hover:text-primary-700">{t('dashboard.quickActions.addUser.title')}</p>
                    <p className="text-sm text-gray-500 group-hover:text-primary-600">{t('dashboard.quickActions.addUser.desc')}</p>
                  </div>
                </div>
              </Link>

              <Link
                to="/employees"
                className="group relative rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 p-6 hover:border-primary-300 hover:bg-primary-50 transition-all duration-200"
              >
                <div className="flex items-center space-x-4">
                  <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center group-hover:bg-primary-200 transition-colors duration-200">
                    <UserGroupIcon className="w-5 h-5 text-primary-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 group-hover:text-primary-700">{t('dashboard.quickActions.manageEmployees.title')}</p>
                    <p className="text-sm text-gray-500 group-hover:text-primary-600">{t('dashboard.quickActions.manageEmployees.desc')}</p>
                  </div>
                </div>
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Recent Activity */}
      {(selectedCompany || isAdmin) && (
        <div className="bg-white rounded-xl shadow-soft border border-gray-200 overflow-hidden">
          <div className="px-6 py-5 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">{t('dashboard.recent.title')}</h3>
            <p className="mt-1 text-sm text-gray-600">{selectedCompany ? t('dashboard.recent.subtitleCompany') : t('dashboard.recent.subtitleSystem')}</p>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {selectedCompany ? (
                // Company-specific activity
                <>
                  <div className="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg">
                    <div className="w-8 h-8 bg-success-100 rounded-full flex items-center justify-center">
                      <ShoppingCartIcon className="w-4 h-4 text-success-600" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">{t('dashboard.recent.orderCreated.title')}</p>
                      <p className="text-sm text-gray-500">{t('dashboard.recent.orderCreated.desc', { id: '12345' })}</p>
                    </div>
                    <span className="text-sm text-gray-400">{t('dashboard.recent.hoursAgo', { count: 2 })}</span>
                  </div>
                  <div className="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg">
                    <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                      <CurrencyDollarIcon className="w-4 h-4 text-primary-600" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">{t('dashboard.recent.saleCompleted.title')}</p>
                      <p className="text-sm text-gray-500">{t('dashboard.recent.saleCompleted.desc', { id: 'INV-2024-001' })}</p>
                    </div>
                    <span className="text-sm text-gray-400">{t('dashboard.recent.hoursAgo', { count: 4 })}</span>
                  </div>
                  <div className="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg">
                    <div className="w-8 h-8 bg-warning-100 rounded-full flex items-center justify-center">
                      <CubeIcon className="w-4 h-4 text-warning-600" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">{t('dashboard.recent.lowStock.title')}</p>
                      <p className="text-sm text-gray-500">{t('dashboard.recent.lowStock.desc', { name: 'Laptop Pro' })}</p>
                    </div>
                    <span className="text-sm text-gray-400">{t('dashboard.recent.hoursAgo', { count: 6 })}</span>
                  </div>
                </>
              ) : (
                // System-wide activity for admin
                <>
                  <div className="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg">
                    <div className="w-8 h-8 bg-success-100 rounded-full flex items-center justify-center">
                      <UsersIcon className="w-4 h-4 text-success-600" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">New user registered</p>
                      <p className="text-sm text-gray-500">John Doe joined the system</p>
                    </div>
                    <span className="text-sm text-gray-400">{t('dashboard.recent.hoursAgo', { count: 2 })}</span>
                  </div>
                  <div className="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg">
                    <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                      <BuildingOfficeIcon className="w-4 h-4 text-primary-600" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">Company updated</p>
                      <p className="text-sm text-gray-500">Tech Solutions Inc. information modified</p>
                    </div>
                    <span className="text-sm text-gray-400">{t('dashboard.recent.hoursAgo', { count: 4 })}</span>
                  </div>
                  <div className="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg">
                    <div className="w-8 h-8 bg-warning-100 rounded-full flex items-center justify-center">
                      <UserGroupIcon className="w-4 h-4 text-warning-600" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">Employee status changed</p>
                      <p className="text-sm text-gray-500">Sarah Manager role updated</p>
                    </div>
                    <span className="text-sm text-gray-400">{t('dashboard.recent.hoursAgo', { count: 6 })}</span>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
